import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Camera, FilePdf, Trash, PaperPlaneTilt, CheckCircle, WarningCircle, LockSimple, ShieldCheck } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/rueckantwort`;
const de = (s) => (s ? `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}` : "");
const eur = (v) => (v == null ? "" : Number(v).toLocaleString("de-DE", { style: "currency", currency: "EUR" }));

// Öffentliche Seite (ohne Anmeldung): unterschriebene Zustimmung zur Mieterhöhung hochladen.
export default function Rueckantwort() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [files, setFiles] = useState([]);
  const [privacy, setPrivacy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const camRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/${token}`)
      .then(async (r) => (r.ok ? setInfo(await r.json()) : setError((await r.json()).detail || "Link ungültig")))
      .catch(() => setError("Verbindung fehlgeschlagen"));
  }, [token]);

  const add = (e) => {
    // FileList ist "live" – vor dem Zurücksetzen des Inputs kopieren.
    const picked = Array.from(e.target.files || []).map((f) => ({ file: f, url: f.type.startsWith("image/") ? URL.createObjectURL(f) : null }));
    e.target.value = "";
    setFiles((p) => [...p, ...picked]);
  };
  const send = async () => {
    setBusy(true); setError("");
    try {
      const fd = new FormData();
      files.forEach((p) => fd.append("files", p.file));
      fd.append("privacy_accepted", "true");
      const r = await fetch(`${API}/${token}/upload`, { method: "POST", body: fd });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Übertragung fehlgeschlagen");
      setDone(data);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  if (done) return (
    <Shell>
      <CheckCircle size={56} weight="fill" className="text-emerald-600 mx-auto" />
      <h1 className="text-xl font-semibold text-center mt-3" data-testid="rueckantwort-done">Vielen Dank!</h1>
      <p className="text-sm text-slate-600 text-center mt-1">Ihre unterschriebene Zustimmung ({done.pages} Seite{done.pages === 1 ? "" : "n"}) ist bei {info?.manager || "Ihrer Hausverwaltung"} eingegangen. Sie können diese Seite schließen.</p>
    </Shell>
  );
  if (error && !info) return (
    <Shell><WarningCircle size={56} className="text-rose-600 mx-auto" /><p className="text-center mt-3 text-slate-800" data-testid="rueckantwort-error">{error}</p></Shell>
  );
  return (
    <Shell>
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold" data-testid="rueckantwort-manager">{info?.manager || "Hausverwaltung"} · Rückantwort</div>
      <h1 className="text-2xl font-semibold text-slate-900 mt-1">Zustimmung zur Mieterhöhung hochladen</h1>
      <SecureNote />
      {info && <Summary info={info} />}
      <p className="text-xs text-slate-500 mt-4">Bitte die unterschriebene Zustimmungserklärung fotografieren (gerade, gut beleuchtet) oder als PDF auswählen. Mehrere Seiten sind möglich. Eine Anmeldung ist nicht nötig.</p>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={add} data-testid="rueckantwort-camera-input" />
      <input ref={fileRef} type="file" accept="application/pdf,image/*" multiple className="hidden" onChange={add} data-testid="rueckantwort-file-input" />
      <div className="grid grid-cols-2 gap-2 mt-3">
        <button type="button" onClick={() => camRef.current?.click()} className="h-14 rounded-xl bg-slate-900 text-white font-medium flex items-center justify-center gap-2 active:scale-[0.98] transition-transform" data-testid="rueckantwort-camera"><Camera size={20} /> Foto aufnehmen</button>
        <button type="button" onClick={() => fileRef.current?.click()} className="h-14 rounded-xl border border-slate-300 bg-white font-medium flex items-center justify-center gap-2 active:scale-[0.98] transition-transform" data-testid="rueckantwort-file"><FilePdf size={20} /> Datei wählen</button>
      </div>
      {files.length > 0 && (
        <>
          <div className="text-xs text-slate-600 mt-4" data-testid="rueckantwort-count">{files.length} Datei{files.length === 1 ? "" : "en"} ausgewählt</div>
          <div className="grid grid-cols-3 gap-2 mt-2" data-testid="rueckantwort-thumbs">
            {files.map((p, i) => (
              <div key={`${p.file.name}-${i}`} className="relative rounded-lg overflow-hidden border border-slate-200 aspect-[3/4] bg-slate-100 flex items-center justify-center">
                {p.url ? <img src={p.url} alt={`Seite ${i + 1}`} className="w-full h-full object-cover" /> : <span className="text-[10px] p-1 text-center break-all flex flex-col items-center gap-1"><FilePdf size={22} className="text-rose-600" />{p.file.name}</span>}
                <span className="absolute bottom-1 left-1 bg-white/90 rounded px-1 text-[10px]">S. {i + 1}</span>
                <button type="button" onClick={() => setFiles((s) => s.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-white/90 rounded p-1 text-rose-700" data-testid={`rueckantwort-remove-${i}`}><Trash size={12} /></button>
              </div>
            ))}
          </div>
        </>
      )}
      <Privacy info={info} checked={privacy} onChange={setPrivacy} />
      {error && <p className="text-sm text-rose-700 mt-3" data-testid="rueckantwort-send-error">{error}</p>}
      <button type="button" disabled={!files.length || !privacy || busy} onClick={send} className="mt-4 w-full h-14 rounded-xl bg-emerald-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-40" data-testid="rueckantwort-send">
        <PaperPlaneTilt size={20} /> {busy ? "Überträgt…" : "Zustimmung sicher senden"}
      </button>
    </Shell>
  );
}

function SecureNote() {
  return (
    <div className="mt-3 flex gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900" data-testid="rueckantwort-secure">
      <LockSimple size={18} weight="fill" className="shrink-0 mt-0.5" />
      <span><b>Sichere Übertragung:</b> Die Verbindung ist TLS-verschlüsselt (https). Ihre Dateien werden ausschließlich an Ihre Hausverwaltung übermittelt, in Deutschland/EU gespeichert und nicht an Dritte weitergegeben. Der Link ist persönlich und nur für diese Mieterhöhung gültig.</span>
    </div>
  );
}

function Row({ label, children, strong }) {
  return <div className="flex justify-between gap-3"><span className="text-slate-500">{label}</span><span className={`text-right ${strong ? "font-semibold text-slate-900" : ""}`}>{children}</span></div>;
}

function Summary({ info }) {
  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3 text-sm space-y-1" data-testid="rueckantwort-info">
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Ihre Mieterhöhung im Überblick</div>
      {info.tenants?.length > 0 && <Row label="Mieter">{info.tenants.join(", ")}</Row>}
      <Row label="Objekt">{info.address || info.object}{info.unit ? ` · ${info.unit}` : ""}</Row>
      {info.contract_number && <Row label="Mietvertrag">{info.contract_number}</Row>}
      {info.case_number && <Row label="Aktenzeichen">{info.case_number}</Row>}
      {info.letter_date && <Row label="Schreiben vom">{de(info.letter_date)}</Row>}
      <div className="border-t border-slate-100 my-1.5" />
      <Row label="Bisherige Nettokaltmiete">{eur(info.current_rent)}</Row>
      <Row label="Neue Nettokaltmiete" strong>{eur(info.new_rent)}</Row>
      {info.increase_eur != null && <Row label="Erhöhung">{eur(info.increase_eur)}{info.increase_pct != null ? ` (${String(info.increase_pct).replace(".", ",")} %)` : ""}</Row>}
      <Row label="Wirksam ab">{de(info.effective_date)}</Row>
      <Row label="Zustimmung bis" strong>{de(info.consent_deadline)}</Row>
      {info.already_uploaded && <div className="text-emerald-700 text-xs pt-1">Es wurde bereits eine Zustimmung hochgeladen. Sie können bei Bedarf eine neue Fassung senden.</div>}
    </div>
  );
}

function Privacy({ info, checked, onChange }) {
  const c = info?.company || {};
  const contact = [c.company_name, c.company_street, [c.company_zip, c.company_city].filter(Boolean).join(" "), c.company_email].filter(Boolean).join(", ");
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-700" data-testid="rueckantwort-privacy">
      <details>
        <summary className="cursor-pointer font-medium text-slate-900 flex items-center gap-1.5" data-testid="rueckantwort-privacy-toggle"><ShieldCheck size={16} /> Datenschutzhinweise (Art. 13 DSGVO)</summary>
        <div className="mt-2 space-y-1.5 leading-relaxed">
          <p><b>Verantwortlich:</b> {contact || "Ihre Hausverwaltung"}.</p>
          <p><b>Zweck & Rechtsgrundlage:</b> Entgegennahme Ihrer Zustimmungserklärung zur Mieterhöhung (§ 558b BGB) zur Durchführung des Mietvertrags, Art. 6 Abs. 1 lit. b DSGVO.</p>
          <p><b>Daten:</b> die hochgeladenen Fotos/PDF-Dateien und der Zeitpunkt des Uploads. Es werden keine Tracking- oder Werbe-Cookies verwendet.</p>
          <p><b>Speicherdauer:</b> für die Dauer des Mietverhältnisses und der gesetzlichen Aufbewahrungsfristen (§ 147 AO, § 257 HGB).</p>
          <p><b>Ihre Rechte:</b> Auskunft, Berichtigung, Löschung, Einschränkung, Widerspruch sowie Beschwerde bei einer Datenschutz-Aufsichtsbehörde.</p>
        </div>
      </details>
      <label className="mt-3 flex items-start gap-2 cursor-pointer">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 accent-emerald-600" data-testid="rueckantwort-privacy-checkbox" />
        <span>Ich habe die Datenschutzhinweise gelesen und bin mit der Übermittlung meiner Unterlagen an die Hausverwaltung einverstanden.</span>
      </label>
    </div>
  );
}

function Shell({ children }) {
  return <div className="min-h-screen bg-stone-50 px-5 py-8"><div className="max-w-md mx-auto">{children}</div></div>;
}
